if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.isSubmitting = false;
        if (this.variantIdInput) this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        // Mutex lock against rapid double-clicks and button disabled state
        if (this.isSubmitting || (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true')) {
          return;
        }

        // --- VARIANT SELECTION GUARD ---
        // Prevents default variant from leaking into cart if user hasn't explicitly selected a size
        const sizeOptionsExist = document.querySelector('.size-button, .size-options, .uk-size-section');
        const activeSizeButton = document.querySelector('.size-button.active:not(.product-form__error-message .size-button)');
        const currentVariantId = this.variantIdInput ? this.variantIdInput.value : '';

        if ((sizeOptionsExist && !activeSizeButton) || !currentVariantId || currentVariantId.trim() === '') {
          this.handleErrorMessage('Please select a size');
          if (typeof window.showSizeSelectionError === 'function') {
            window.showSizeSelectionError();
          }
          return;
        }

        this.isSubmitting = true;
        this.error = false;
        this.handleErrorMessage();

        if (this.submitButton) {
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.classList.add('loading');
        }
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname + window.location.search);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const previousCartCount = window.cartItemCount || 0;

        fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            let responseData;
            try {
              responseData = await response.json();
            } catch (jsonErr) {
              responseData = {
                status: response.status || 500,
                message: 'Server error',
                description: 'Could not add item to cart. Please try again.',
              };
            }

            if (!response.ok || responseData.status) {
              this.error = true;
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: responseData.errors || responseData.description,
                message: responseData.message,
              });

              try {
                if (typeof mixpanel !== 'undefined') {
                  mixpanel.track('Add to Cart Failed', {
                    product_id: formData.get('id'),
                    error_status: responseData.status,
                    error_message: responseData.message,
                    error_description: responseData.description,
                    page: window.location.pathname,
                  });
                }
              } catch (e) {}

              // Check if it's an inventory/quantity error
              const errorMsg = responseData.description || responseData.message || '';
              const isInventoryError =
                errorMsg.toLowerCase().includes('inventory') ||
                errorMsg.toLowerCase().includes('stock') ||
                errorMsg.toLowerCase().includes('quantity') ||
                errorMsg.toLowerCase().includes('available');

              if (isInventoryError) {
                this.showInventoryLimitPopup(errorMsg);
              } else {
                this.handleErrorMessage(responseData.description);
              }

              const soldOutMessage = this.submitButton ? this.submitButton.querySelector('.sold-out-message') : null;
              if (soldOutMessage) {
                this.submitButton.setAttribute('aria-disabled', true);
                if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              }
              return;
            }

            this.error = false;

            // 1. Publish Dawn internal PubSub event
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: responseData,
            });

            // 2. Dispatch Shopify Standard Storefront Event
            try {
              document.dispatchEvent(
                new CustomEvent('shopify:cart:lines-update', {
                  bubbles: true,
                  detail: {
                    action: 'add',
                    lines: [{ id: formData.get('id'), quantity: formData.get('quantity') || 1 }],
                    cart: responseData,
                  },
                })
              );
            } catch (e) {}

            // 3. Wardrobe builder sync (safe, non-blocking)
            try {
              let wbKey = null;
              for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && key.startsWith('wb-state-')) {
                  wbKey = key;
                  break;
                }
              }

              if (wbKey) {
                const wbStateRaw = sessionStorage.getItem(wbKey);
                if (wbStateRaw) {
                  const stateArray = JSON.parse(wbStateRaw);
                  const stateMap = new Map(stateArray);

                  const productId = (responseData.product_id || '').toString();
                  const priceFormatted = '₹' + ((responseData.price || 0) / 100).toLocaleString('en-IN');

                  let size = '';
                  if (responseData.options_with_values && responseData.options_with_values.length > 0) {
                    const sizeOpt = responseData.options_with_values.find((o) => o.name && o.name.toLowerCase() === 'size');
                    if (sizeOpt) size = sizeOpt.value;
                  }
                  if (!size && responseData.variant_options && responseData.variant_options.length > 0) {
                    size = responseData.variant_options[0];
                  }

                  if (productId) {
                    stateMap.set(productId, {
                      variantId: (responseData.variant_id || formData.get('id')).toString(),
                      title: responseData.product_title || responseData.title || '',
                      price: priceFormatted,
                      priceCents: responseData.price || 0,
                      image: responseData.image || '',
                      size: size,
                    });
                    sessionStorage.setItem(wbKey, JSON.stringify(Array.from(stateMap.entries())));
                  }
                }
              }
            } catch (err) {
              console.warn('Wardrobe sync failed:', err);
            }

            // 4. Decoupled, non-blocking Mixpanel tracking
            setTimeout(() => {
              try {
                if (typeof mixpanel !== 'undefined') {
                  const newCartCount = responseData.cart
                    ? responseData.cart.item_count
                    : previousCartCount + parseInt(formData.get('quantity') || 1);
                  const cartCountChanged = newCartCount !== previousCartCount;

                  let addedFrom = 'pdp';
                  try {
                    for (let i = 0; i < sessionStorage.length; i++) {
                      const key = sessionStorage.key(i);
                      if (key && key.startsWith('wb-state-')) {
                        addedFrom = 'wardrobe_builder_pdp';
                        break;
                      }
                    }
                  } catch (e) {}

                  mixpanel.track('Add to Cart', {
                    product_id: formData.get('id'),
                    product_title: responseData.product_title,
                    variant_id: formData.get('id'),
                    quantity: formData.get('quantity') || 1,
                    price: (responseData.price || 0) / 100,
                    sku: responseData.sku,
                    cart_item_count_before: previousCartCount,
                    cart_item_count_after: newCartCount,
                    cart_count_changed: cartCountChanged,
                    ab_variant: window.SLAY_AB ? window.SLAY_AB.variant : 'unknown',
                    ab_review_variant: window.SLAY_AB_REVIEW ? window.SLAY_AB_REVIEW.variant : 'unknown',
                    satc_ab_variant: window.SLAY_SATC_AB ? window.SLAY_SATC_AB.variant : (window.__SLAY_SATC_VARIANT || 'unknown'),
                    added_from: addedFrom,
                  });

                  if (typeof mixpanel.start_session_recording === 'function' && Math.random() < 0.75) {
                    mixpanel.start_session_recording();
                  }

                  window.cartItemCount = newCartCount;
                }
              } catch (e) {
                console.warn('Mixpanel tracking failed:', e);
              }
            }, 0);

            // 5. Render cart drawer
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(responseData);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(responseData);
            }
          })
          .catch((e) => {
            console.error('Add to cart request failed:', e);
            this.error = true;
            this.handleErrorMessage('Failed to add item. Please try again.');
          })
          .finally(() => {
            this.isSubmitting = false;
            if (this.submitButton) {
              this.submitButton.classList.remove('loading');
              if (!this.error) {
                this.submitButton.removeAttribute('aria-disabled');
              }
            }
            // Only remove empty state if there was no error
            if (this.cart && !this.error && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
            const spinner = this.querySelector('.loading__spinner');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      showInventoryLimitPopup(errorMsg) {
        // Create toast container if it doesn't exist
        let toast = document.getElementById('inventory-limit-toast');
        if (!toast) {
          toast = document.createElement('div');
          toast.id = 'inventory-limit-toast';
          toast.innerHTML = `
            <style>
              #inventory-limit-toast {
                position: fixed;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                z-index: 10000;
                display: none;
              }
              #inventory-limit-toast.active {
                display: block;
              }
              .inventory-toast-content {
                background: #ffffff;
                color: #111827;
                border: 1px solid #e5e7eb;
                padding: 10px 16px;
                border-radius: 12px;
                font-size: 14px;
                font-weight: 500;
                box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
                max-width: 320px;
                text-align: center;
              }
            </style>
            <div class="inventory-toast-content">
              No more inventory for this available, sorry.
            </div>
          `;
          document.body.appendChild(toast);
        }

        // Show toast briefly
        toast.classList.add('active');
        clearTimeout(this.inventoryToastTimeout);
        this.inventoryToastTimeout = setTimeout(() => {
          toast.classList.remove('active');
        }, 3000);
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
